import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  SafeAreaView,
  ScrollView,
} from 'react-native';

/**
 * ForgotPassword Component
 * 
 * This component provides a UI for users to reset their password.
 * Users need to enter their username and email to request a password reset.
 * 
 * Props:
 * @param {Object} imagePaths - Contains paths for images (logo and email icon).
 * @param {Function} onResetPassword - Function to handle password reset.
 * @param {Function} onGoToLogin - Function to navigate to the login page.
 */
const ForgotPassword = ({ imagePaths, onResetPassword, onGoToLogin }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [errorUsername, setErrorUsername] = useState('');
  const [errorEmail, setErrorEmail] = useState('');

  const emailInputRef = useRef(null);

  // Function to validate user inputs
  const validateInputs = () => {
    let errors = {};

    if (!username.trim()) {
      errors.username = 'Username is required.';
    } else if (username.length < 3) {
      errors.username = 'Username must be at least 3 characters.';
    } else if (username.length > 15) {
      errors.username = 'Username must be less than 15 characters.';
    } else if (!/^[A-Za-z\s_]+$/.test(username)) {
      errors.username = 'Please enter a valid name (only letters and spaces are allowed).';
    }

    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }

    setErrorUsername(errors.username || '');
    setErrorEmail(errors.email || '');

    return Object.keys(errors).length === 0;
  };

  // Function to handle password reset request
  const handleResetPassword = () => {
    if (!validateInputs()) return;
    onResetPassword(email,username);
  };

  return (
    <SafeAreaView style={styles.container} testID="forgotPasswordContainer">
      <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false} testID="forgotPasswordScrollView">
        <View style={styles.content}>
          {/* Logo */}
          <View style={styles.logoContainer} testID="logoContainer">
            <Image source={{ uri: imagePaths.logo }} style={styles.logo} resizeMode="contain" testID="logoImage" />
          </View>

          {/* Form */}
          <View style={styles.formContainer} testID="formContainer">
            <Text style={styles.header} testID="headerText">Forgot Password</Text>
            <Text style={styles.description} testID="descriptionText">Enter your username and email to receive password reset instructions.</Text>

            {/* Username Input */}
            <View style={styles.inputWrapper} testID="usernameInputWrapper">
              <Text style={styles.label} testID="usernameLabel">User Name</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  placeholder="Enter username"
                  value={username}
                  onChangeText={setUsername}
                  autoCapitalize="none"
                  placeholderTextColor="#333"
                  returnKeyType="next"
                  onSubmitEditing={() => emailInputRef.current?.focus()}
                  testID="usernameInput"
                />
              </View>
              {errorUsername ? <Text style={styles.errorText} testID="usernameError">{errorUsername}</Text> : null}
            </View>

            {/* Email Input */}
            <View style={styles.inputWrapper} testID="emailInputWrapper">
              <Text style={styles.label} testID="emailLabel">Email</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  ref={emailInputRef}
                  style={styles.input}
                  placeholder="Enter your email"
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  placeholderTextColor="#333"
                  testID="emailInput"
                />
                <Image source={{ uri: imagePaths.emailIcon }} style={styles.icon} resizeMode="contain" testID="emailIcon" />
              </View>
              {errorEmail ? <Text style={styles.errorText} testID="emailError">{errorEmail}</Text> : null}
            </View>

            {/* Reset Password Button */}
            <TouchableOpacity style={styles.resetButton} onPress={handleResetPassword} testID="resetButton">
              <Text style={styles.resetButtonText}>Reset Password</Text>
            </TouchableOpacity>

            {/* Go to Login */}
            <TouchableOpacity onPress={onGoToLogin} testID="goToLoginButton">
              <Text style={styles.loginText}>Go to Login</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F7FA', padding: 10, paddingTop: 0 ,    fontFamily: 'GT-Walsheim' 
  },
  scrollContainer: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 15, paddingTop: 0,    fontFamily: 'GT-Walsheim' 
  },
  content: { maxWidth: 400, width: '100%',    fontFamily: 'GT-Walsheim' 
  },
  logoContainer: { alignItems: 'center', marginBottom: 44 ,    fontFamily: 'GT-Walsheim' 
  },
  logo: { width: 220, height: 90, marginBottom: 8 ,    fontFamily: 'GT-Walsheim' 
  },
  formContainer: { width: '100%' ,    fontFamily: 'GT-Walsheim' 
  },
  header: { fontSize: 24, fontWeight: '600', color: '#000', marginBottom: 16, textAlign: 'center' ,    fontFamily: 'GT-Walsheim' 
  },
  description: { fontSize: 14, color: '#666', textAlign: 'center', marginBottom: 20 ,     fontFamily: 'GT-Walsheim' 
  },
  inputWrapper: { marginBottom: 16 ,    fontFamily: 'GT-Walsheim' 
  },
  label: { fontSize: 14, color: '#000', marginBottom: 8, fontWeight: '500' ,    fontFamily: 'GT-Walsheim' 
  },
  inputContainer: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#ddd', borderRadius: 8, backgroundColor: '#fff', paddingHorizontal: 12, height: 45 ,    fontFamily: 'GT-Walsheim' 
  },
  input: { flex: 1, fontSize: 16, color: '#000', backgroundColor: '#fff', outlineStyle: 'none',    fontFamily: 'GT-Walsheim' 
  },
  errorText: { color: 'red', fontSize: 12, marginTop: 5 ,    fontFamily: 'GT-Walsheim' 
  },
  resetButton: { backgroundColor: '#000', paddingVertical: 14, borderRadius: 8, alignItems: 'center', marginTop: 20,    fontFamily: 'GT-Walsheim' 
  },
  resetButtonText: { color: '#fff', fontSize: 16, fontWeight: '600',    fontFamily: 'GT-Walsheim' 
  },
  icon: { width: 20, height: 20, marginLeft: 10,    fontFamily: 'GT-Walsheim' 
  },
  loginText: { fontSize: 14, color: '#000', textAlign: 'center', marginTop: 15, textDecorationLine: 'underline',    fontFamily: 'GT-Walsheim' 
  },
});

export default ForgotPassword;